// Unified, client-safe certificate-verification contract for all six grading
// companies (PSA, BGS, CGC, SGC, ACE, TAG).
//
// Everything here is pure: types, statuses, cert-number sanitising, official
// URL builders, the SSRF host allowlist and UI copy. Network access lives in
// `cert-verification.server.ts` so this module can be unit tested and imported
// from the browser without leaking server behaviour.
//
// PROVIDER ACCESS REALITY (probed live 2026-08-02, re-check before changing):
//   PSA  — official public API `api.psacard.com/publicapi` (token required).
//          The consumer website is behind a Cloudflare interstitial, so the API
//          is the only permitted automated path.
//   BGS  — Beckett publishes no open certificate API; the official lookup page
//          is a client-rendered app with no server-side record. Automated
//          lookup therefore requires written partner access (BGS_API_TOKEN).
//   CGC  — cgccards.com/certlookup is behind a Cloudflare challenge (HTTP 403
//          `cf-mitigated: challenge`). Solving it would be an anti-bot bypass,
//          so we do not. Requires partner API access (CGC_API_TOKEN).
//   SGC  — gosgc.com cert lookup is an Angular SPA guarded by reCAPTCHA
//          Enterprise. Same reasoning as CGC (SGC_API_TOKEN).
//   ACE  — official certificate page is server-rendered with a structured
//          record. Fully working without credentials.
//   TAG  — api.taggrading.com returns 401 for anonymous callers and
//          my.taggrading.com is a client-rendered SPA. Requires TAG_API_TOKEN.

export type CertProvider = "psa" | "bgs" | "cgc" | "sgc" | "ace" | "tag";

export const CERT_PROVIDER_SLUGS = ["psa", "bgs", "cgc", "sgc", "ace", "tag"] as const;

export type CertVerificationStatus =
  | "verified"
  | "not_found"
  | "malformed"
  | "temporarily_unavailable"
  | "rate_limited"
  | "upstream_blocked"
  | "parser_failed"
  | "manual_official_link"
  | "manual_review_required"
  | "unsupported";

/** How the result was obtained. Never claim more than the transport allows. */
export type CertVerificationMethod =
  | "official_partner_api"
  | "official_public_api"
  | "official_lookup_endpoint"
  | "official_page"
  | "manual_official_link"
  | "none";

export type CertSubgrades = {
  centering?: number | null;
  corners?: number | null;
  edges?: number | null;
  surface?: number | null;
  autograph?: number | null;
};

/** One normalised shape returned by every provider adapter. */
export type NormalizedCertResult = {
  provider: CertProvider;
  certificateNumber: string;
  status: CertVerificationStatus;
  /** True only for `status === "verified"` against an official source. */
  verified: boolean;
  cardName: string | null;
  year: number | null;
  setName: string | null;
  cardNumber: string | null;
  variant: string | null;
  language: string | null;
  grade: string | null;
  subgrades: CertSubgrades | null;
  population: number | null;
  officialUrl: string;
  verifiedAt: string | null;
  method: CertVerificationMethod;
  /** 0–1 confidence in the extracted record; 0 for every non-verified status. */
  confidence: number;
  /** Machine-readable reason for every non-verified status. */
  failureReason: string | null;
  /** Human-readable copy for the UI. */
  message: string;
  /** SHA-256 of the upstream payload; set server-side, never the payload itself. */
  fingerprint?: string | null;
  cached?: boolean;
  /** Seconds the caller should wait before retrying (rate limit / Retry-After). */
  retryAfterSeconds?: number | null;
  /** Upstream request id, when the provider returns one. */
  providerRequestId?: string | null;
  /** How many retries the transport used (0 = first attempt succeeded/settled). */
  retryCount?: number;
};


export const CERT_PROVIDER_LABEL: Record<CertProvider, string> = {
  psa: "PSA",
  bgs: "BGS",
  cgc: "CGC",
  sgc: "SGC",
  ace: "ACE Grading",
  tag: "TAG Grading",
};

// --- SSRF allowlist ---------------------------------------------------------

export const ALLOWED_CERT_HOSTS = [
  "api.psacard.com",
  "www.psacard.com",
  "www.beckett.com",
  "api.beckett.com",
  "www.cgccards.com",
  "api.cgccards.com",
  "gosgc.com",
  "www.gosgc.com",
  "api.gosgc.com",
  "acegrading.com",
  "www.acegrading.com",
  "api.taggrading.com",
  "my.taggrading.com",
] as const;

export function isAllowedCertUrl(url: string): boolean {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return false;
  }
  if (u.protocol !== "https:") return false;
  return (ALLOWED_CERT_HOSTS as readonly string[]).includes(u.hostname.toLowerCase());
}

// --- Cert number sanitising -------------------------------------------------

/** Strip everything that can never appear in a certificate number. */
export function sanitiseCertNumber(raw: string): string {
  return raw
    .normalize("NFKC")
    .replace(/[^A-Za-z0-9-]/g, "")
    .toUpperCase()
    .slice(0, 32);
}

/**
 * Per-provider normalisation. Formats are deliberately permissive so a grading
 * company changing its numbering does not hard-fail the platform.
 */
export function normaliseCertNumber(provider: CertProvider, raw: string): string | null {
  const s = sanitiseCertNumber(raw);
  if (!s) return null;
  switch (provider) {
    case "psa":
      return /^[0-9]{6,12}$/.test(s) ? s : null;
    case "bgs": {
      const b = s.replace(/^BGS-?/, "").replace(/^BVG-?/, "");
      return /^[0-9]{6,12}$/.test(b) ? b : null;
    }
    case "cgc": {
      const c = s.replace(/^CGC-?/, "");
      return /^[0-9A-Z]{6,16}$/.test(c) ? c : null;
    }
    case "sgc": {
      const g = s.replace(/^SGC-?/, "");
      return /^[0-9]{6,12}(-[0-9]{1,4})?$/.test(g) ? g : null;
    }
    case "ace": {
      const a = s.replace(/^#/, "");
      return /^[0-9]{4,12}$/.test(a) ? a : null;
    }
    case "tag": {
      const t = s.replace(/^TAG-?/, "");
      return /^[A-Z0-9-]{4,20}$/.test(t) ? t : null;
    }
  }
}

/** Official, user-openable certificate page for each grading company. */
export function officialCertUrl(provider: CertProvider, cert: string): string {
  const c = encodeURIComponent(cert);
  switch (provider) {
    case "psa":
      return `https://www.psacard.com/cert/${c}`;
    case "bgs":
      // Beckett's official lookup form posts `item_id` + `item_type`.
      return `https://www.beckett.com/grading/card-lookup?item_id=${c}&item_type=BGS`;
    case "cgc":
      return `https://www.cgccards.com/certlookup/${c}/`;
    case "sgc":
      // Verified live 2026-08-02: /certificate-verification 404s; this is the
      // current official SGC cert lookup page.
      return `https://gosgc.com/cert-code-lookup?cert=${c}`;

    case "ace":
      return `https://acegrading.com/cert/${c}`;
    case "tag":
      return `https://my.taggrading.com/card/${c}`;
  }
}

// --- Status copy ------------------------------------------------------------

export const CERT_STATUS_MESSAGE: Record<CertVerificationStatus, string> = {
  verified: "Verified against the official certificate record.",
  not_found: "Certificate not found in the official grading-company record.",
  malformed: "That certificate number does not look valid for this grading company.",
  temporarily_unavailable:
    "The grading company's verification service is temporarily unavailable. Please try again or open the official certificate page.",
  rate_limited: "Verification limit reached. Please wait and try again.",
  manual_official_link:
    "Automated verification is not currently authorised for this grading company. Open the official certificate page to confirm the record.",
  upstream_blocked:
    "The grading company blocks automated verification. Open the official certificate page to confirm the record yourself.",
  parser_failed:
    "The official record was reached, but BidNova could not read the response reliably.",
  manual_review_required:
    "Automated verification is not authorised for this grading company. Open the official certificate page to confirm the record yourself.",
  unsupported: "Certificate verification is not available for this grading company.",
};

/** Non-verified results all fall back to a manual, clearly-labelled path. */
export function isManualPath(status: CertVerificationStatus): boolean {
  return (
    status === "manual_review_required" ||
    status === "manual_official_link" ||
    status === "upstream_blocked" ||
    status === "parser_failed" ||
    status === "unsupported"
  );
}

export function emptyResult(
  provider: CertProvider,
  cert: string,
  status: Exclude<CertVerificationStatus, "verified">,
  method: CertVerificationMethod,
  failureReason: string,
  officialUrl = officialCertUrl(provider, cert),
  retryAfterSeconds: number | null = null,
): NormalizedCertResult {
  return {
    provider,
    certificateNumber: cert,
    status,
    verified: false,
    cardName: null,
    year: null,
    setName: null,
    cardNumber: null,
    variant: null,
    language: null,
    grade: null,
    subgrades: null,
    population: null,
    officialUrl,
    verifiedAt: null,
    method,
    confidence: 0,
    failureReason,
    message: CERT_STATUS_MESSAGE[status],
    retryAfterSeconds,
    providerRequestId: null,
  };
}

// --- Shared parsing helpers -------------------------------------------------

const ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  "#39": "'",
  apos: "'",
  nbsp: " ",
};

/** Decode the small entity set upstream pages emit, then strip all markup. */
export function decodeAndStrip(value: string): string {
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/&(#\d+|[a-zA-Z]+);/g, (_m, k: string) => {
      if (ENTITIES[k]) return ENTITIES[k];
      if (k.startsWith("#")) {
        const code = Number(k.slice(1));
        return Number.isFinite(code) && code >= 32 && code < 0x10ffff
          ? String.fromCodePoint(code)
          : " ";
      }
      return " ";
    })
    .replace(/\s+/g, " ")
    .trim();
}

/** Bounded, escaped field value — never trust upstream length or markup. */
export function certField(value: unknown, max = 120): string | null {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value !== "string") return null;
  const clean = decodeAndStrip(value).slice(0, max).trim();
  return clean.length ? clean : null;
}

export function certYear(value: unknown): number | null {
  const s = certField(value, 8);
  if (!s) return null;
  const m = /(19|20)\d{2}/.exec(s);
  return m ? Number(m[0]) : null;
}

function num(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value)))
    return Number(value);
  return null;
}

/** Normalise a provider payload's subgrade block; returns null when empty. */
export function normaliseSubgrades(raw: unknown): CertSubgrades | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const out: CertSubgrades = {
    centering: num(r["centering"] ?? r["Centering"] ?? r["center"]),
    corners: num(r["corners"] ?? r["Corners"]),
    edges: num(r["edges"] ?? r["Edges"]),
    surface: num(r["surface"] ?? r["Surface"]),
    autograph: num(r["autograph"] ?? r["Autograph"] ?? r["autographGrade"]),
  };
  return Object.values(out).some((v) => v !== null && v !== undefined) ? out : null;
}

/** Detect an anti-bot interstitial so we report `upstream_blocked`, not 404. */
export function looksLikeBotChallenge(body: string): boolean {
  const b = body.slice(0, 4000).toLowerCase();
  return (
    b.includes("just a moment") ||
    b.includes("cf-browser-verification") ||
    b.includes("challenges.cloudflare.com") ||
    b.includes("enable javascript and cookies to continue") ||
    b.includes("recaptcha/enterprise")
  );
}

// --- Buyer-facing badge state ----------------------------------------------

/**
 * Exactly one buyer-facing status per listing. Never render two at once —
 * callers switch on this single value.
 */
export type CertBadgeState =
  | "officially_verified"
  | "verification_pending"
  | "manual_official_check"
  | "not_independently_verified";

export const CERT_BADGE_COPY: Record<CertBadgeState, { title: string; detail: string }> = {
  officially_verified: {
    title: "Officially Verified",
    detail: "Matched against the grading company's official certificate record.",
  },
  verification_pending: {
    title: "Verification Pending",
    detail:
      "The grading company's verification service was unavailable. BidNova will re-check this certificate automatically.",
  },
  manual_official_check: {
    title: "Manual Official Check",
    detail:
      "This grading company does not authorise automated verification. Open the official certificate page to confirm the record yourself.",
  },
  not_independently_verified: {
    title: "Not Independently Verified",
    detail: "These grading details were entered by the seller and have not been confirmed.",
  },
};

/** Derive the single badge state from the stored listing verification fields. */
export function certBadgeState(input: {
  certNumber?: string | null;
  verified?: boolean | null;
  status?: string | null;
}): CertBadgeState | null {
  if (!input.certNumber) return null;
  if (input.verified) return "officially_verified";
  const s = (input.status ?? "").toLowerCase();
  if (s === "temporarily_unavailable" || s === "rate_limited" || s === "pending")
    return "verification_pending";
  if (
    s === "manual_official_link" ||
    s === "manual_review_required" ||
    s === "upstream_blocked" ||
    s === "parser_failed" ||
    s === "unsupported"
  )
    return "manual_official_check";
  return "not_independently_verified";
}
